import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../../../services/api';

export interface LineItemOverrideModalProps {
  pronumber: number | string;
  /** The line item being overridden. Required — the TYPE is the override's identity. */
  liitem: string;
  /** Provenance only; 0/undefined when the charge isn't on the invoice. */
  lineItemRecno?: number;
  /** The standing override, when editing one. Its presence switches the dialog from
   *  "record a decision" to "revise or withdraw the decision" — clicking the lock on an
   *  overridden line opens this rather than silently deleting the record, so wording can
   *  be corrected without losing the original author and date. */
  existing?: {
    recno: number;
    reason: string;
    /** Additional Comments on the standing override. Older rows have none. */
    comments?: string;
    userid: string;
    overrideDateTime: string | null;
  } | null;
  saving?: boolean;
  error?: string | null;
  /** `comments` is '' when none were given. Trimmed. */
  onConfirm: (reason: string, comments: string) => void;
  /** Withdraw the override entirely. Only offered when editing one. */
  /** Omit to hide "Remove override" — the caller decides whether withdrawing is still
   *  allowed. Passed as undefined once the order is invoiced: the charge was not billed
   *  and cannot be now, so revoking would not undo anything, only erase the record of a
   *  decision that already had its effect. */
  onDelete?: () => void;
  /** Explains a missing Remove button. Without it the control just disappears, which
   *  reads as a bug rather than as a rule. */
  deleteBlockedReason?: string;
  /** VIEW the decision without being able to change it. Recording a not-billing decision
   *  is an LC's call (`invoice-override`, migration 537) while READING one is not — the
   *  lock on an overridden line opens for everybody so a reader can see who decided and
   *  why, which is the whole reason that button is not permission-gated. Without this the
   *  dialog would offer Save and Remove to a reader whose request the server refuses. */
  readOnly?: boolean;
  onClose: () => void;
}

/**
 * Records a decision not to let the estimate/reconciler touch a charge.
 *
 * The reason is mandatory and comes from cms.unbilled_reasons, the same user-maintained
 * list the Unbilled Charges page uses. It was free text until 2026-09-18, and the prod
 * data is why it is not any more: of 247 live overrides, 159 said one thing nine
 * different ways ("for convenance not billable", "for convenance not billbale", "for ops
 * convenance nnot billabe", …) with "convenience" misspelled in every variant. Nothing
 * could be reported off that.
 *
 * THE SELECTED TEXT IS STORED, not the row's recno. The lookup has a UNIQUE key on
 * `reason`, so the text IS its natural key and a recno would buy nothing — except on a
 * rename or a delete, which is exactly when you want the wording as it stood when the
 * decision was made rather than silently rewritten across every historical override.
 * `unbilled_charge_reviews.lc_reason` already stores it the same way.
 *
 * The server still accepts any text (see invoice_reconciler/overrides.py): the 247
 * pre-existing reasons are all off-list, and an API that rejected them would make its
 * own history uneditable.
 *
 * ADDITIONAL COMMENTS (migration 495) are the free-text half. The reason says WHAT
 * happened; the comment says WHICH one. Always shown, optional — unless the reason is
 * flagged "Require Comments" in Data Management, in which case Save stays disabled
 * until something is typed. The server enforces the same rule, so this is the polite
 * version, not the only one. An off-list standing reason matches no flag and so
 * requires nothing.
 */

export interface UnbilledReasonOption {
  reason: string;
  requireComments: boolean;
  responsibleDepartment: string;
}
export const LineItemOverrideModal: React.FC<LineItemOverrideModalProps> = ({
  pronumber,
  liitem,
  lineItemRecno,
  existing = null,
  saving = false,
  error = null,
  onConfirm,
  onDelete,
  deleteBlockedReason,
  readOnly = false,
  onClose,
}) => {
  const isEdit = !!existing;
  const [reason, setReason] = useState(existing?.reason ?? '');
  const [comments, setComments] = useState(existing?.comments ?? '');
  const trimmed = reason.trim();
  const trimmedComments = comments.trim();

  // The list is small (8 rows today) and user-maintained, so it is fetched per open
  // rather than cached — a reason added in Data Management should be pickable without
  // a reload. A failure leaves the select empty rather than blocking the dialog: the
  // standing reason still shows, and Cancel still works.
  const [reasons, setReasons] = useState<UnbilledReasonOption[]>([]);
  const [loadingReasons, setLoadingReasons] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get<UnbilledReasonOption[]>('/cms-lookups/unbilled-reasons')
      .then(({ data }) => { if (!cancelled) setReasons(Array.isArray(data) ? data : []); })
      .catch(() => { /* leave it empty; the stored reason is still shown below */ })
      .finally(() => { if (!cancelled) setLoadingReasons(false); });
    return () => { cancelled = true; };
  }, []);

  // A standing reason that is not (or no longer) on the list stays selectable, so
  // opening an old override to change nothing cannot silently blank it. Every one of
  // the 247 overrides written before this became a list is in exactly that position.
  const storedOffList = !!existing?.reason && !reasons.some((r) => r.reason === existing.reason);

  // Whether the chosen reason demands a comment. Off-list (or not yet loaded) = no.
  const selectedReason = reasons.find((r) => r.reason === trimmed);
  const commentsRequired = !!selectedReason?.requireComments;
  const responsibleDepartment = selectedReason?.responsibleDepartment?.trim();
  const missingComments = commentsRequired && trimmedComments.length === 0;
  const canSave = trimmed.length > 0 && !missingComments && !saving;

  const rootRef = useRef<HTMLDivElement | null>(null);
  // Alt+S confirms (this one IS a write, unlike the estimate's "stage"); Alt+C closes.
  useAltHotkeys(
    { S: () => !readOnly && canSave && onConfirm(trimmed, trimmedComments), C: onClose },
    rootRef,
    { layer: true },
  );

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label="Override line item"
      // z-210 sits above the Invoice Estimate (z-200), which stays open behind this so
      // the user keeps the numbers they're deciding about in view. The scrim is lighter
      // than a standalone modal's for the same reason: stacked on the estimate's own
      // /50 it would dim the context to near-black.
      className="fixed inset-0 z-[210] flex items-center justify-center bg-scrim/30 p-4"
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl w-full max-w-lg flex flex-col"
      >
        <ModalHeader
          title={
            <span className="flex items-center gap-2">
              <span>{isEdit ? 'Edit Override' : 'Override Line Item'}</span>
              <span className="text-sm font-normal text-slate-300">· PRO {pronumber}</span>
            </span>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-label">
            Invoice Reconciliation will stop adding, changing or removing{' '}
            <span className="font-semibold">{liitem}</span> on this pro.
          </p>
          {isEdit && (
            <p className="text-[0.6875rem] text-muted">
              Set by <span className="font-medium">{existing!.userid}</span>
              {existing!.overrideDateTime
                ? ` on ${new Date(existing!.overrideDateTime).toLocaleString()}`
                : ''}
              . Editing keeps that attribution — only the wording changes.
            </p>
          )}
          <p className="text-[0.6875rem] text-muted">
            This applies to the line item <em>type</em> on this pro, so it holds even if
            the line isn&apos;t on the invoice — which is how you record that a charge
            deliberately isn&apos;t being billed.
          </p>

          <label className="block">
            <span className="block text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-1">
              Reason <span className="text-red-600 dark:text-red-400">*</span>
            </span>
            <select
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              aria-label="Override reason"
              disabled={saving}
              className="w-full rounded border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm text-slate-900 dark:text-gray-100 disabled:opacity-50"
            >
              <option value="">{loadingReasons ? 'Loading…' : 'Select a reason…'}</option>
              {/* The standing reason first when it is off-list, so re-saving an old
                  override cannot quietly replace it with a list value nobody chose. */}
              {storedOffList && (
                <option value={existing!.reason}>{existing!.reason} (existing)</option>
              )}
              {reasons.map((r) => (
                <option key={r.reason} value={r.reason}>{r.reason}</option>
              ))}
            </select>
            <span className="mt-1 block text-[0.625rem] text-muted">
              Required — it&apos;s the only thing that will explain this decision to
              whoever finds it later. Maintained in Data Management under Override
              Reasons.
            </span>
          </label>

          <label className="block">
            <span className="block text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-1">
              Responsible Department
            </span>
            <input
              aria-label="Responsible Department"
              aria-readonly="true"
              readOnly
              value={responsibleDepartment ?? ''}
              placeholder={loadingReasons ? 'Loading…' : 'No department assigned'}
              className="w-full cursor-default rounded border border-slate-300 bg-slate-100 px-2 py-1 text-sm text-slate-700 focus:border-slate-300 focus:outline-none focus:ring-0 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:focus:border-gray-600"
            />
          </label>

          <label className="block">
            <span className="block text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-1">
              Additional Comments
              {commentsRequired && <span className="text-red-600 dark:text-red-400"> *</span>}
            </span>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              aria-label="Additional comments"
              disabled={saving}
              rows={3}
              maxLength={2000}
              placeholder={commentsRequired
                ? 'Say what specifically happened…'
                : 'Optional — anything the reason alone doesn’t say.'}
              className={`w-full rounded border px-2 py-1 text-sm text-slate-900 dark:text-gray-100 bg-white dark:bg-gray-900 disabled:opacity-50 ${
                missingComments
                  ? 'border-red-400 dark:border-red-600'
                  : 'border-slate-300 dark:border-gray-600'
              }`}
            />
            {missingComments && (
              <span className="mt-1 block text-[0.625rem] text-red-600 dark:text-red-400">
                &ldquo;{trimmed}&rdquo; requires Additional Comments before it can be saved.
              </span>
            )}
          </label>

          <p className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[0.6875rem] text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
            <ExclamationTriangleIcon className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>
              This holds in GWI360. Saves that still route through the legacy CMS can
              recalculate this charge until the save cutover completes.
            </span>
          </p>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-200">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          {/* Withdrawing is a deliberate, separate action — the lock no longer deletes
              on a second click, so a mis-worded override can be fixed instead of lost. */}
          <div>
            {readOnly && (
              <span className="text-xs italic text-gray-500 dark:text-gray-400">
                Recorded by the LC — you can read this decision but not change it.
              </span>
            )}
            {!readOnly && isEdit && onDelete && (
              <button
                onClick={onDelete}
                disabled={saving}
                className="px-3 py-2 text-sm rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/30"
              >
                Remove override
              </button>
            )}
            {!readOnly && isEdit && !onDelete && deleteBlockedReason && (
              <span className="text-xs italic text-gray-500 dark:text-gray-400">
                {deleteBlockedReason}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-label hover:bg-highlight"
          >
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button
              onClick={() => canSave && onConfirm(trimmed, trimmedComments)}
              disabled={!canSave}
              className="px-4 py-2 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : isEdit ? 'Save' : 'Override'}
            </button>
          )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LineItemOverrideModal;
